import { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = ({ selectedStartDate, selectedEndDate, onDateSelect, minDate, maxDate }) => {
  // Always initialize to today's month
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [startDate, setStartDate] = useState(selectedStartDate);
  const [endDate, setEndDate] = useState(selectedEndDate);

  useEffect(() => {
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
    // If a date is selected, optionally navigate to that month (but don't override initial load)
    // We'll keep it showing today's month initially for better UX
  }, [selectedStartDate, selectedEndDate]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    if (date < today) return true;
    return false;
  };

  // Normalize date to midnight for comparison
  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const normalizedDate = normalizeDate(date);
    const normalizedStart = normalizeDate(startDate);
    const normalizedEnd = normalizeDate(endDate);
    return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
  };

  const isDateSelected = (date) => {
    const normalizedDate = normalizeDate(date);
    if (startDate && endDate) {
      const normalizedStart = normalizeDate(startDate);
      const normalizedEnd = normalizeDate(endDate);
      return normalizedDate.getTime() === normalizedStart.getTime() || 
             normalizedDate.getTime() === normalizedEnd.getTime();
    }
    if (startDate) {
      const normalizedStart = normalizeDate(startDate);
      return normalizedDate.getTime() === normalizedStart.getTime();
    }
    return false;
  };

  const handleDateClick = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    date.setHours(0, 0, 0, 0);
    if (isDateDisabled(date)) return;

    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setStartDate(date);
      setEndDate(null);
      onDateSelect({ startDate: date, endDate: null });
    } else if (startDate && !endDate) {
      // Complete the range
      const normalizedStart = normalizeDate(startDate);
      const normalizedDate = normalizeDate(date);
      if (normalizedDate < normalizedStart) {
        // Selected date is before start, make it the new start
        setEndDate(startDate);
        setStartDate(date);
        onDateSelect({ startDate: date, endDate: startDate });
      } else {
        // Selected date is after start, make it the end
        setEndDate(date);
        onDateSelect({ startDate, endDate: date });
      }
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Get previous month's days to fill the first week
  const prevMonthDays = getDaysInMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const days = [];
  
  // Previous month's trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, day);
    days.push({ day, date, isCurrentMonth: false });
  }
  
  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    days.push({ day, date, isCurrentMonth: true });
  }
  
  // Next month's leading days to fill the last week
  const remainingDays = 42 - days.length; // 6 weeks * 7 days
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, day);
    days.push({ day, date, isCurrentMonth: false });
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900">{monthName}</h3>
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {days.map(({ day, date, isCurrentMonth }, index) => {
          const disabled = isDateDisabled(date);
          const selected = isDateSelected(date);
          const isToday = 
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();

          const normalizedDate = normalizeDate(date);
          const inRange = isDateInRange(date) && !disabled && isCurrentMonth;
          const isStart = startDate && normalizeDate(startDate).getTime() === normalizedDate.getTime();
          const isEnd = endDate && normalizeDate(endDate).getTime() === normalizedDate.getTime();

          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              disabled={disabled || !isCurrentMonth}
              className={`
                aspect-square text-sm rounded-lg transition-all relative
                ${disabled || !isCurrentMonth
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-emerald-50 cursor-pointer'
                }
                ${selected
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 font-semibold'
                  : ''
                }
                ${inRange && !selected
                  ? 'bg-emerald-100 text-emerald-900'
                  : ''
                }
                ${isToday && !selected && !inRange
                  ? 'ring-2 ring-emerald-300'
                  : ''
                }
              `}
              type="button"
            >
              {day}
              {isStart && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-800"></span>}
              {isEnd && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-800"></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DatePicker;

